import { enableProdMode, provideZoneChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import { AppModule } from "./app/app.module";
import { environment } from "./environments/environment";
import DevExpressConfig from "devextreme/core/config";
import { registerHXRInterceptors } from "./xhr-interceptos";
import { registerFilePondPlugins } from "src/filepond-setup";
import themes from "devextreme/ui/themes";
import * as Sentry from "@sentry/angular";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isBetween from "dayjs/plugin/isBetween";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isToday from "dayjs/plugin/isToday";
import { compact } from "lodash";
import * as DevExpress from "devextreme/ui/data_grid";
import DateBox from "devextreme/ui/date_box";

import { sntLog } from "src/app/common/logger";
import { shouldDropSentryError } from "src/app/support/sentry-error-filter";
import {
  applyHttpClientGrouping,
  SENTRY_FAILED_REQUEST_TARGETS
} from "src/app/support/sentry-http-client";
import { filterSentryMetric } from "src/app/support/sentry-metric-filter";
import {
  filterSentryTransaction,
  sampleSentryTrace
} from "src/app/support/sentry-transaction-filter";
import { initOpenPanel } from "src/app/support/openpanel";

const logger = sntLog("Main");
if (
  environment.snoots.production &&
  window.location.toString().indexOf("pms.local.pet") <= 0
) {
  Sentry.init({
    environment: environment.snoots.production ? "prod" : "staging",
    release: require("../package.json").version,
    dsn: "https://856221db5c214df792352d8fabaf42a9@o1270807.ingest.sentry.io/6586904",
    integrations: [
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: {
          serializeAttribute: "data-testid"
        },
        fetch: true,
        history: true,
        xhr: true
      }),
      Sentry.httpClientIntegration({
        failedRequestTargets: SENTRY_FAILED_REQUEST_TARGETS
      }),
      Sentry.httpContextIntegration(),
      Sentry.browserTracingIntegration({
        enableLongAnimationFrame: false,
        beforeStartSpan: (context) => {
          let name = "/";

          let splitted = compact(context.name.split("/"));

          if (splitted.length >= 2) {
            splitted = splitted.slice(2);
          }

          if (splitted.length > 0) {
            name = splitted
              .map((split: string) => {
                if (/^(?=.*\d)[a-zA-Z0-9]+$/.test(split)) {
                  return ":id";
                }

                return split;
              })
              .join("/");

            name = `/${name}`;
          }

          return {
            ...context,
            name
          };
        }
      })
    ],
    enableLogs: false,
    sendDefaultPii: true,
    ignoreErrors: [
      "HttpErrorResponse",
      "Failed to fetch",
      "TypeError: network error",
      "getDisplaySets",
      "ResizeObserver loop limit"
    ],
    tracePropagationTargets: [
      "backend.snoots.pet",
      "backend.staging.snoots.pet"
    ],
    tracesSampler: (context) =>
      sampleSentryTrace(context, environment.snoots.production),
    beforeSend: async (event: Sentry.ErrorEvent, hint: Sentry.EventHint) => {
      if (shouldDropSentryError(event, hint)) {
        return null;
      }

      applyHttpClientGrouping(event);

      return event;
    },
    beforeSendTransaction: (event) => filterSentryTransaction(event),
    beforeSendMetric: (metric) => filterSentryMetric(metric)
  });

  initOpenPanel();
}

//registerHXRInterceptors({ ignoreUrls: ["snoots-storage-staging-filebucket"] });

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(isToday);

(<any>window)["retry"] = () => {
  return Promise.reject();
};

if (environment.snoots.enableProdMode) {
  enableProdMode();
}

registerFilePondPlugins();

const localStorageThemeValue = <"light" | "dark" | null>(
  localStorage.getItem("theme")
);

if (!localStorageThemeValue || localStorageThemeValue === "light") {
  //themes.current("generic.light");
  themes.current("fluent.saas.light.compact");
} else {
  themes.current("generic.dark");
}

themes.initialized(() => {
  DevExpressConfig({
    editorStylingMode: "filled",
    defaultCurrency: "EUR"
  });

  // HACK
  // https://supportcenter.devexpress.com/ticket/details/t1268473/datagrid-customstore-loadoptions-empty-after-selecting-more-than-24-rows-in-datagrid
  DevExpress.default.defaultOptions({
    device: { deviceType: "desktop" },
    options: {
      selection: {
        maxFilterLengthInRequest: 10000
      }
    }
  });

  // Force calendar picker on all devices so tablet/phone can type dates
  // instead of being locked to the native/rollers dialog.
  DateBox.defaultOptions({
    options: {
      pickerType: "calendar",
      openOnFieldClick: false
    }
  });

  platformBrowserDynamic()
    .bootstrapModule(AppModule, {
      applicationProviders: [provideZoneChangeDetection()]
    })
    .catch((err) => logger.error(err));
});
